import React, { useState, useRef, useEffect } from 'react';
import ReactSelect from 'react-select';

const styles = {
  container: (provided) => ({
    ...provided,
    width: 'auto',
  }),
  control: (provided, {isFocused}) => ({
    ...provided,
    boxShadow: isFocused ? '0 0 0 .2rem rgba(17,10,68,.25)' : 'none',
    width: 'auto',
    minWidth: 'auto',
  }),
  indicatorSeparator: () => {}, // removes the separator line
  menu: (provided) => ({
    ...provided,
    zIndex: 10,
    width: 'max-content',
    minWidth: '100%',
    maxWidth: '100%',
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontWeight: 'bold',
    fontSize: '16px', // Match the default form control font size
    color: '#495057',
    padding: '8px 12px 4px 12px',
    textTransform: 'none',
  }),
  option: (provided, state) => ({
    ...provided,
    paddingLeft: state.data.__isNew__ ? '12px' : '24px', // More padding for grouped options
  }),
}

const theme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: 'rgba(17,10,68,.25)',
    primary: 'rgba(17,10,68,.60)',
  },
})

const SelectInput = (props) => {
  const { value, options, name, placeholder = "", onChange, className, disabled, width, ...otherProps } = props;
  const [currentValue, setCurrentValue] = useState(value);
  const selectRef = useRef(null);

  // Calculate the minimum width based on the longest option text
  const calculateMinWidth = () => {
    if (!options || options.length === 0) return 150;

    let longestText = '';

    // Handle both regular options and grouped options
    options.forEach(option => {
      if (option.options) {
        // It's a group, check the group label first
        const groupLabel = option.label || '';
        if (groupLabel.length > longestText.length) {
          longestText = groupLabel;
        }

        // Then check all options in the group
        option.options.forEach(groupOption => {
          const text = groupOption.label || '';
          // Add extra length for indentation in grouped options
          const textWithIndent = '    ' + text;
          if (textWithIndent.length > longestText.length) {
            longestText = textWithIndent;
          }
        });
      } else {
        // It's a regular option
        const text = option.label || '';
        if (text.length > longestText.length) {
          longestText = text;
        }
      }
    });

    // Approximate character width (in pixels) + padding for dropdown arrow and spacing
    const charWidth = 8;
    const padding = 30; // Space for arrow, padding, etc.
    return Math.max(150, longestText.length * charWidth + padding);
  };  const handleChange = (selectedOption) => {
    setCurrentValue(selectedOption);

    // Update the element's value property so Stimulus can read it
    if (selectRef.current) {
      selectRef.current.value = selectedOption ? selectedOption.value : '';

      // Dispatch a change event so Stimulus detects it automatically
      const changeEvent = new Event('change', { bubbles: true });
      selectRef.current.dispatchEvent(changeEvent);
    }

    if (onChange && typeof onChange === 'function') {
      onChange(selectedOption);
    }
  };

  // Hook to set the initial value in the DOM element
  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.value = currentValue?.value || '';
    }
  }, [currentValue]);

  return (
    <div ref={selectRef} {...otherProps}>
      <ReactSelect
        className="react-select-container"
        classNamePrefix="react-select"
        defaultValue={value}
        noOptionsMessage={({inputValue}) => `${window.I18n.t("search.filters.results_count", {count: 0})}`}
        isClearable={false}
        name={name}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        styles={{
          ...styles,
          control: (provided, state) => ({
            ...styles.control(provided, state),
            width: width || calculateMinWidth(),
            minWidth: width || calculateMinWidth(),
          }),
          menu: (provided) => ({
            ...styles.menu(provided),
            width: width || calculateMinWidth(),
            minWidth: width || calculateMinWidth(),
            maxWidth: width || calculateMinWidth(),
          })
        }}
        theme={theme}
        isDisabled={disabled}
      />
    </div>
  );
}

export default SelectInput;
